/**
 * Z_TPLAN Service
 * API Client für Transportplanung - Verwendet direkte Connector-API
 */

import axios, { type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../../../../config.js';
import { getAuthHeaders } from '../../../../api/auth.js';

const TENANT_ID = '68e43b18e0652b6ee86a59d6';
const CONNECTOR_ID = '695e3b086a4d495a40d195bd'; // SAP HE1 Connector

// Create axios instance with auth headers
const createAxiosInstance = () => {
    const instance = axios.create({
        baseURL: API_BASE_URL,
        withCredentials: true
    });

    // Add auth headers to every request
    instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        const authHeaders = getAuthHeaders();
        Object.assign(config.headers, authHeaders);
        return config;
    });

    return instance;
};

/**
 * Führt eine SAP-Tabellen-Abfrage über den direkten Connector durch
 */
async function fetchSapTable<T = any>(
    tableName: string,
    filters: Array<{
        FIELD: string;
        OP: 'EQ' | 'NE' | 'GT' | 'LT' | 'GE' | 'LE' | 'CP' | 'BT';
        LOW: string;
        HIGH?: string;
    }> = [],
    timeoutMs = 60000
): Promise<T[]> {
    const RANGE_TAB = filters.map((filter) => ({
        TABLE: tableName,
        FIELD: filter.FIELD,
        S: 'I', // Include
        OP: filter.OP,
        LOW: filter.LOW,
        HIGH: filter.HIGH || ''
    }));

    const requestPayload = {
        connectorId: CONNECTOR_ID,
        request: {
            request: 'SAP',
            payload: {
                IT_INPUT: [
                    {
                        TABLE_NAME: tableName,
                        RANGE_TAB
                    }
                ]
            }
        },
        timeoutMs
    };

    console.log(`[ZTPlanService] Fetching SAP table ${tableName}:`, {
        filters,
        connectorId: CONNECTOR_ID
    });

    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.post('/api/connectors/request-sap', requestPayload);

    if (!response.data.success) {
        throw new Error(`SAP table fetch failed: ${response.data.error || 'Unknown error'}`);
    }

    console.log(`[ZTPlanService] SAP table ${tableName} fetched:`, {
        recordCount: response.data.data?.length || 0,
        durationMs: response.data.durationMs
    });

    return response.data.data || [];
}

export interface TransportPlanningFilter {
    tenantId?: string;
    trandi?: string;
    cw?: string;
    year?: string;
    frac?: string;
    werks?: string[];
    // Contract-Filter wie im ABAP
    vertrag?: string;      // p_cont
    lifnr?: string;        // p_supp (Lieferant/Spediteur)
    inco1?: string;        // p_inco (Incoterms)
    ct_frac?: string;      // p_ct_fra (Contract-Fraktion)
}

export interface Transport {
    TRANSPORTNUMMER: string;
    KALENDERWOCHE: string;
    JAHR: string;
    WERKS: string;
    TPLST?: string;  // Transportdispostelle
    FRAKTION: string;
    FRAKTION_BEZ?: string;  // Fraktionsbezeichnung (angereichert aus ZHB_T_BPLAN)
    BEDARF_ID?: string;
    VERTRAG?: string;
    VERTRAGSPOSITION?: string;
    VERTART?: number;  // Vertragsart (1=extern, 2=Werkverkehr)
    SPEDITEUR?: string;
    SPED_BEZ?: string;  // Spediteurname (angereichert aus ZHB_TLIEF1)
    MENGE: number;
    GEPLANTE_MENGE: number;
    AB_STRASSE?: boolean;  // Transport "ab Straße" (ohne Vertrag)
    STATUS?: string;  // Display: Icon (@08@, @09@, @0A@)
    createdAt?: string;
    updatedAt?: string;
    type?: string;
}

export interface Demand {
    BEDARF_ID: string;
    TRANSPORTDISPOSTELLE: string;
    KALENDERWOCHE: string;
    JAHR: string;
    WERKS: string;
    FRAKTION: string;
    FRAKTION_BEZ: string;
    MENGE: number;  // LKW-Ladungen (im ABAP: MENGE)
    type?: string;
    tenantId?: string;
}

export interface Contract {
    VERTRAG: string;
    LIFNR: string;
    INCO1: string;
    WERKS: string;
    DATVON: string;
    DATBIS: string;
}

export interface ContractPosition {
    VERTRAG: string;
    VERTPOS: string;
    MATNR: string;
    MAKTX?: string;  // Material Text (kommt aus ZHB_VERTPOS_ANZ View)
    STATUS: string;
    VENDE_DAT?: string;  // Vertragsende Datum
    VERTART?: number;  // Vertragsart
    LIFNR?: string;  // Lieferant
    NAME1?: string;  // Lieferantenname (kommt aus ZHB_VERTPOS_ANZ View)
}

export interface Supplier {
    LIFNR: string;
    NAME1: string;
}

export interface ZTPlanData {
    demand: Demand[];
    transports: Transport[];
    contracts: Contract[];
    contractPositions: ContractPosition[];
    suppliers: Supplier[];
}

export class ZTPlanService {
    /**
     * Synchronisiert Daten aus SAP direkt über Connector
     */
    static async syncData(filter: TransportPlanningFilter) {
        console.log('[ZTPlanService] syncData called with filter:', filter);
        const startTime = Date.now();

        try {
            const syncedData: any = {
                zhb_t30: [],
                zhb_t_bplan: [],
                zhb_tvert1: [],
                zhb_tvert2: [],
                zhb_t_tplan: [],
                zhb_t_karenzzeit: [],
                zhb_tlief1: [],
                zhb_t36: []
                // zhb_t_lkw_load wird im ABAP nur für INSERT/DELETE verwendet, nicht gelesen
            };

            // SCHRITT 1: Stammdaten (klein, keine Abhängigkeiten)
            console.log('[ZTPlanService] Schritt 1: Lade Stammdaten...');

            // 1a. ZHB_T30 - Transportdispostellen
            const t30Filters = [];
            if (filter.trandi) {
                t30Filters.push({ FIELD: 'TPLST', OP: 'EQ' as const, LOW: filter.trandi });
            }
            syncedData.zhb_t30 = await fetchSapTable('ZHB_T30', t30Filters);

            // 1b. ZHB_T36 - Fraktionen/Sortimente
            const t36Filters = [];
            if (filter.frac) {
                t36Filters.push({ FIELD: 'FRAKTION', OP: 'EQ' as const, LOW: filter.frac });
            }
            syncedData.zhb_t36 = await fetchSapTable('ZHB_T36', t36Filters);

            // 1c. ZHB_T_KARENZZEIT - Karenzzeiten (WICHTIG: VOR Verträgen laden!)
            console.log('[ZTPlanService] Lade Karenzzeiten für Werke...');
            const karenzFilters: Array<{ FIELD: string; OP: 'EQ'; LOW: string }> = [];
            if (filter.werks && filter.werks.length > 0 && filter.werks.length <= 10) {
                filter.werks.forEach(werk => {
                    karenzFilters.push({ FIELD: 'WERKS', OP: 'EQ' as const, LOW: werk });
                });
            }
            syncedData.zhb_t_karenzzeit = await fetchSapTable('ZHB_T_KARENZZEIT', karenzFilters);

            // Erstelle Karenzzeit-Map (Werk -> Karenzzeit in Tagen)
            const karenzMap = new Map<string, number>();
            syncedData.zhb_t_karenzzeit.forEach((k: any) => {
                karenzMap.set(k.WERKS, k.KARENZZEIT || 0);
            });

            // SCHRITT 2: Bedarfsplanung laden und Werke extrahieren
            console.log('[ZTPlanService] Schritt 2: Lade Bedarfsplanung...');
            const bplanFilters = [];
            if (filter.trandi) {
                bplanFilters.push({ FIELD: 'TPLST', OP: 'EQ' as const, LOW: filter.trandi });
            }
            if (filter.cw) {
                // WICHTIG: Bedarfsplanung wird nur für die EXAKTE Kalenderwoche geladen (wie im ABAP)
                // Der ±4 Bereich gilt nur für Transporte, nicht für Bedarf!
                bplanFilters.push({ FIELD: 'KALENDERWOCHE', OP: 'EQ' as const, LOW: filter.cw });
            }
            if (filter.year) {
                bplanFilters.push({ FIELD: 'JAHR', OP: 'EQ' as const, LOW: filter.year });
            }
            if (filter.frac) {
                bplanFilters.push({ FIELD: 'FRAKTION', OP: 'EQ' as const, LOW: filter.frac });
            }
            // Optional: WERKS-Filter wenn vom Benutzer vorgegeben
            if (filter.werks && filter.werks.length > 0 && filter.werks.length <= 10) {
                filter.werks.forEach(werk => {
                    bplanFilters.push({ FIELD: 'WERKS', OP: 'EQ' as const, LOW: werk });
                });
            }
            syncedData.zhb_t_bplan = await fetchSapTable('ZHB_T_BPLAN', bplanFilters);

            // Extrahiere eindeutige WERKS aus Bedarfsplanung
            const werksSet = new Set<string>();
            syncedData.zhb_t_bplan.forEach((bplan: any) => {
                if (bplan.WERKS) werksSet.add(bplan.WERKS);
            });
            const werks = Array.from(werksSet);
            console.log(`[ZTPlanService] Gefundene Werke: ${werks.join(', ')}`);

            // SCHRITT 3: Verträge für diese Werke holen (MIT KARENZZEIT-LOGIK!)
            console.log('[ZTPlanService] Schritt 3: Lade Verträge mit Karenzzeit-Logik...');

            // Heute als YYYYMMDD
            const today = new Date();
            const todayStr = today.toISOString().split('T')[0].replace(/-/g, '');

            const tvert1Filters: Array<{ FIELD: string; OP: "EQ" | "NE" | "GT" | "LT" | "GE" | "LE" | "CP" | "BT"; LOW: string }> = [
                // DATVON < heute (Vertrag muss begonnen haben)
                { FIELD: 'DATVON', OP: 'LT' as const, LOW: todayStr }
            ];

            // Contract-Filter wie im ABAP (p_cont, p_supp, p_inco)
            if (filter.vertrag) {
                tvert1Filters.push({ FIELD: 'VERTRAG', OP: 'EQ' as const, LOW: filter.vertrag });
            }
            if (filter.lifnr) {
                tvert1Filters.push({ FIELD: 'LIFNR', OP: 'EQ' as const, LOW: filter.lifnr });
            }
            if (filter.inco1) {
                tvert1Filters.push({ FIELD: 'INCO1', OP: 'EQ' as const, LOW: filter.inco1 });
            }

            // Für jedes Werk: DATBIS >= (heute - karenzzeit) ODER ABFUHRDAT >= heute
            // Problem: SAP unterstützt kein OR in RANGE_TAB direkt
            // Lösung: Hole Verträge pro Werk separat
            syncedData.zhb_tvert1 = [];

            for (const werk of werks) {
                const karenzzeit = karenzMap.get(werk) || 0;
                const datbisLimit = new Date(today);
                datbisLimit.setDate(datbisLimit.getDate() - karenzzeit);
                const datbisLimitStr = datbisLimit.toISOString().split('T')[0].replace(/-/g, '');

                const werkFilters = [
                    ...tvert1Filters,
                    { FIELD: 'WERKS', OP: 'EQ' as const, LOW: werk },
                    // DATBIS >= (heute - karenzzeit) - nehmen wir als Hauptfilter
                    { FIELD: 'DATBIS', OP: 'GE' as const, LOW: datbisLimitStr }
                ];

                console.log(`[ZTPlanService] Lade Verträge für Werk ${werk} (Karenzzeit: ${karenzzeit} Tage, DATBIS >= ${datbisLimitStr})`);
                const werkVertraege = await fetchSapTable('ZHB_TVERT1', werkFilters);
                syncedData.zhb_tvert1.push(...werkVertraege);

                // Alternative: ABFUHRDAT >= heute (für Verträge die bald abfuhr haben)
                // TODO: Könnte man noch hinzufügen wenn nötig
            }

            console.log(`[ZTPlanService] Verträge geladen: ${syncedData.zhb_tvert1.length}`);

            // SCHRITT 4: Vertragspositionen NUR für die gefundenen Verträge (wie ABAP Zeile 196-218)
            console.log('[ZTPlanService] Schritt 4: Lade Vertragspositionen für gefundene Verträge...');
            const vertragSet = new Set<string>();
            syncedData.zhb_tvert1.forEach((vert: any) => {
                if (vert.VERTRAG) vertragSet.add(vert.VERTRAG);
            });
            const vertraege = Array.from(vertragSet).sort();
            console.log(`[ZTPlanService] Gefundene Verträge: ${vertraege.length} - ${vertraege.slice(0, 5).join(', ')}${vertraege.length > 5 ? '...' : ''}`);

            const tvert2Filters: Array<{ FIELD: string; OP: "EQ" | "NE" | "GT" | "LT" | "GE" | "LE" | "CP" | "BT"; LOW: string; HIGH?: string }> = [
                // STATUS = space (nur offene Positionen)
                { FIELD: 'STATUS', OP: 'EQ' as const, LOW: ' ' },
                // VENDE_DAT = space (keine Enddatum gesetzt)
                { FIELD: 'VENDE_DAT', OP: 'EQ' as const, LOW: ' ' }
            ];

            // Optional: Contract-Fraktion Filter
            if (filter.ct_frac) {
                tvert2Filters.push({ FIELD: 'CONTRACT_FRAC', OP: 'EQ' as const, LOW: filter.ct_frac });
            }
            if (vertraege.length > 0) {
                if (vertraege.length <= 50) {
                    // Bis zu 50 Verträge: Einzelne EQ-Filter
                    vertraege.forEach(vertrag => {
                        tvert2Filters.push({ FIELD: 'VERTRAG', OP: 'EQ' as const, LOW: vertrag });
                    });
                } else {
                    // Mehr als 50: Bereich-Filter (BT) mit Min/Max
                    const minVertrag = vertraege[0];
                    const maxVertrag = vertraege[vertraege.length - 1];
                    console.log(`[ZTPlanService] Verwende Bereichsfilter: ${minVertrag} bis ${maxVertrag}`);
                    tvert2Filters.push({ FIELD: 'VERTRAG', OP: 'BT' as const, LOW: minVertrag, HIGH: maxVertrag });
                }
                syncedData.zhb_tvert2 = await fetchSapTable('ZHB_TVERT2', tvert2Filters);

                // Nachfiltern im Frontend bei Bereichsfilter (um Lücken zu vermeiden)
                if (vertraege.length > 50) {
                    const vertragSetForFilter = new Set(vertraege);
                    syncedData.zhb_tvert2 = syncedData.zhb_tvert2.filter((pos: any) =>
                        vertragSetForFilter.has(pos.VERTRAG)
                    );
                }
            } else {
                syncedData.zhb_tvert2 = [];
            }

            // SCHRITT 5: Transportplanung für diese Werke und Zeitraum
            console.log('[ZTPlanService] Schritt 5: Lade Transportplanung...');
            const tplanFilters = [];
            if (filter.cw) {
                const cwNum = parseInt(filter.cw);
                const cwMin = Math.max(1, cwNum - 4).toString();
                const cwMax = Math.min(53, cwNum + 4).toString();
                tplanFilters.push({ FIELD: 'KALENDERWOCHE', OP: 'BT' as const, LOW: cwMin, HIGH: cwMax });
            }
            if (filter.year) {
                tplanFilters.push({ FIELD: 'JAHR', OP: 'EQ' as const, LOW: filter.year });
            }
            if (filter.frac) {
                tplanFilters.push({ FIELD: 'FRAKTION', OP: 'EQ' as const, LOW: filter.frac });
            }
            // WERKS-Filter basierend auf Bedarfsplanung
            if (werks.length > 0 && werks.length <= 10) {
                werks.forEach(werk => {
                    tplanFilters.push({ FIELD: 'WERKS', OP: 'EQ' as const, LOW: werk });
                });
            }
            syncedData.zhb_t_tplan = await fetchSapTable('ZHB_T_TPLAN', tplanFilters);

            // SCHRITT 6: Lieferanten/Spediteure - NUR für Spediteure aus Transportplanung
            // (ZHB_T_LKW_LOAD wird nicht geladen - nur INSERT/DELETE in ABAP, keine SELECT)
            console.log('[ZTPlanService] Schritt 6: Lade Lieferanten für verwendete Spediteure...');
            const spediteurSet = new Set<string>();
            syncedData.zhb_t_tplan.forEach((tplan: any) => {
                if (tplan.SPEDITEUR) spediteurSet.add(tplan.SPEDITEUR);
            });
            const spediteure = Array.from(spediteurSet).sort();
            console.log(`[ZTPlanService] Gefundene Spediteure: ${spediteure.length} - ${spediteure.slice(0, 5).join(', ')}${spediteure.length > 5 ? '...' : ''}`);

            const liefFilters = [];
            if (spediteure.length > 0) {
                // Filter nach LEISTKL und STATUS wie im ABAP
                liefFilters.push({ FIELD: 'LEISTKL', OP: 'EQ' as const, LOW: '0010' });
                liefFilters.push({ FIELD: 'STATUS', OP: 'EQ' as const, LOW: '' });

                if (spediteure.length <= 50) {
                    // Einzelne LIFNR-Filter
                    spediteure.forEach(sped => {
                        liefFilters.push({ FIELD: 'LIFNR', OP: 'EQ' as const, LOW: sped });
                    });
                } else {
                    // Bereichsfilter
                    const minSped = spediteure[0];
                    const maxSped = spediteure[spediteure.length - 1];
                    liefFilters.push({ FIELD: 'LIFNR', OP: 'BT' as const, LOW: minSped, HIGH: maxSped });
                }
                syncedData.zhb_tlief1 = await fetchSapTable('ZHB_TLIEF1', liefFilters);

                // Nachfiltern im Frontend bei Bereichsfilter
                if (spediteure.length > 50) {
                    const spediteurSetForFilter = new Set(spediteure);
                    syncedData.zhb_tlief1 = syncedData.zhb_tlief1.filter((lief: any) =>
                        spediteurSetForFilter.has(lief.LIFNR)
                    );
                }
            } else {
                syncedData.zhb_tlief1 = [];
            }

            const durationMs = Date.now() - startTime;

            console.log('[ZTPlanService] Sync completed:', {
                durationMs,
                recordCounts: {
                    zhb_t30: syncedData.zhb_t30.length,
                    zhb_t_bplan: syncedData.zhb_t_bplan.length,
                    zhb_tvert1: syncedData.zhb_tvert1.length,
                    zhb_tvert2: syncedData.zhb_tvert2.length,
                    zhb_t_tplan: syncedData.zhb_t_tplan.length,
                    zhb_t_karenzzeit: syncedData.zhb_t_karenzzeit.length,
                    zhb_tlief1: syncedData.zhb_tlief1.length,
                    zhb_t36: syncedData.zhb_t36.length
                    // zhb_t_lkw_load removed - not needed for reading
                }
            });

            return {
                success: true,
                data: syncedData,
                durationMs
            };
        } catch (error) {
            console.error('[ZTPlanService] Sync error:', error);
            throw error;
        }
    }

    /**
     * Holt Daten direkt aus SAP (keine MongoDB mehr)
     * Verwendet die gleiche Logik wie syncData
     */
    static async getData(filter: TransportPlanningFilter): Promise<ZTPlanData> {
        console.log('[ZTPlanService] getData - hole live aus SAP mit Filter:', filter);

        // Rufe syncData auf um live aus SAP zu holen
        const result = await this.syncData(filter);

        if (!result.success) {
            throw new Error('Failed to fetch data from SAP');
        }

        // Transformiere die Daten ins erwartete Format
        return {
            demand: result.data.zhb_t_bplan || [],
            transports: result.data.zhb_t_tplan || [],
            contracts: result.data.zhb_tvert1 || [],
            contractPositions: result.data.zhb_tvert2 || [],
            suppliers: result.data.zhb_tlief1 || []
        };
    }

    /**
     * Erstellt einen neuen Transport
     */
    static async createTransport(transport: Omit<Transport, 'TRANSPORTNUMMER'>) {
        console.log('[ZTPlanService] createTransport called with data:', transport);
        const requestData = {
            tenantId: TENANT_ID,
            transport
        };

        try {
            const axiosInstance = createAxiosInstance();
            const response = await axiosInstance.post('/api/ztplan/transport', requestData);
            console.log('[ZTPlanService] Transport created:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ZTPlanService] createTransport error:', error);
            throw error;
        }
    }

    /**
     * Aktualisiert einen Transport
     */
    static async updateTransport(transportNumber: string, updates: Partial<Transport>) {
        console.log('[ZTPlanService] updateTransport called:', { transportNumber, updates });
        const requestData = {
            tenantId: TENANT_ID,
            updates
        };

        try {
            const axiosInstance = createAxiosInstance();
            const response = await axiosInstance.put(`/api/ztplan/transport/${transportNumber}`, requestData);
            console.log('[ZTPlanService] Transport updated:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ZTPlanService] updateTransport error:', error);
            throw error;
        }
    }

    /**
     * Löscht einen Transport
     */
    static async deleteTransport(transportNumber: string) {
        console.log('[ZTPlanService] deleteTransport called for:', transportNumber);
        const url = `/api/ztplan/transport/${transportNumber}?tenantId=${TENANT_ID}`;

        try {
            const axiosInstance = createAxiosInstance();
            const response = await axiosInstance.delete(url);
            console.log('[ZTPlanService] Transport deleted:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ZTPlanService] deleteTransport error:', error);
            throw error;
        }
    }

    /**
     * Berechnet Transport-Status
     */
    static getTransportStatus(transport: Transport): 'complete' | 'partial' | 'empty' {
        if (transport.GEPLANTE_MENGE === transport.MENGE) {
            return 'complete';
        } else if (transport.GEPLANTE_MENGE > 0 && transport.GEPLANTE_MENGE < transport.MENGE) {
            return 'partial';
        } else {
            return 'empty';
        }
    }
}
